/* eslint-disable */
// Session 55 · Alpha Launch Final Gate — Go / No-Go Readiness Report.
// Auto-generated aggregator that pulls signals from all previous phases
// and renders an Executive Summary + Blockers + Priorities + Known Issues
// + Alpha Recommendation. Print-first so Cam can archive a PDF snapshot
// per Wave.

import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const VERDICT_META = {
  go: {label: "✅ GO", color: "#83B26E", bg: "linear-gradient(135deg, #2A4B26 0%, #43703E 100%)"},
  go_with_conditions: {label: "⚠️ GO WITH CONDITIONS", color: "#B8913A", bg: "linear-gradient(135deg, #8A6B26 0%, #B8913A 100%)"},
  no_go: {label: "❌ NO-GO", color: "#C05353", bg: "linear-gradient(135deg, #7A2323 0%, #C05353 100%)"},
};

export default function AdminGoNoGo() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/go-no-go");
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading) return <div style={{padding:40}}>Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;
  if (!data) return <div style={{padding:40}} data-testid="gng-loading">Generating report…</div>;

  const v = VERDICT_META[data.verdict] || VERDICT_META.go_with_conditions;

  return (
    <div style={{padding:"32px 40px", maxWidth:1000, margin:"0 auto", fontFamily:"Jost, sans-serif", color:"#1B2340"}} data-testid="admin-go-no-go">
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16}} className="no-print">
        <div>
          <span className="m-eyebrow" style={{color:"var(--gold)"}}>Admin · Alpha Launch Report</span>
          <h1 style={{fontFamily:"Cormorant Garamond, serif", fontSize:34, margin:"4px 0 0", fontWeight:500}}>Go / No-Go</h1>
          <div style={{fontSize:11, color:"rgba(27,35,64,0.5)", marginTop:4}}>
            Generated {new Date(data.generated_at).toLocaleString()}
          </div>
        </div>
        <div style={{display:"flex", gap:10}}>
          <button type="button" onClick={() => window.print()} data-testid="gng-print"
            style={{padding:"7px 16px", background:"#1B2340", color:"#FAF8F3", border:"none", borderRadius:999, fontSize:12, cursor:"pointer"}}>🖨 Print</button>
          <Link to="/admin/command-center" style={{color:"#B8913A", fontSize:13, alignSelf:"center"}}>← Command Center</Link>
        </div>
      </div>

      {err && <div style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginBottom:16}}>{err}</div>}

      {/* VERDICT hero */}
      <div style={{background: v.bg, color:"#FAF8F3", padding:"32px 30px", borderRadius:14, marginBottom:24}} data-testid="gng-verdict">
        <div style={{fontSize:11, letterSpacing:"0.18em", textTransform:"uppercase", color:"rgba(255,255,255,0.6)"}}>Executive Summary</div>
        <div style={{fontSize:38, fontFamily:"Cormorant Garamond, serif", fontWeight:500, marginTop:6}} data-testid="gng-verdict-label">{v.label}</div>
        <div style={{fontSize:15, marginTop:10, lineHeight:1.6, opacity:0.94}}>{data.summary}</div>

        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:14, marginTop:22}}>
          <HeroStat label="Recommended Wave 1" value={data.recommended_wave1 || "—"} testid="gng-wave1" />
          <HeroStat label="Wave duration" value={`${data.wave1_duration_days} days`} testid="gng-duration" />
          <HeroStat label="Readiness" value={`${data.checklist?.ready_percent || 0}%`} testid="gng-readiness" />
          <HeroStat label="Blockers" value={data.blockers?.length || 0} testid="gng-blocker-count" />
        </div>
      </div>

      {/* CHECKLIST GRID */}
      <Section title="Alpha Readiness Checklist" testid="gng-section-checklist">
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:8}}>
          {data.checklist?.areas && Object.entries(data.checklist.areas).map(([area, info]) => {
            const c = info.status === "green" ? "#83B26E" : info.status === "yellow" ? "#B8913A" : "#C05353";
            return (
              <div key={area} data-testid={`gng-area-${area}`} style={{
                padding:"10px 12px", background:"#fff", border:"1px solid rgba(184,145,58,0.2)",
                borderLeft:`3px solid ${c}`, borderRadius:8,
              }}>
                <div style={{fontSize:13, fontWeight:500}}>{area}</div>
                <div style={{fontSize:11, color:"rgba(27,35,64,0.6)", marginTop:2}}>{info.detail}</div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* BLOCKERS */}
      {data.blockers?.length > 0 && (
        <Section title="🛑 Critical Blockers" testid="gng-section-blockers"
          copy="These prevent Alpha launch. All must be resolved before invitations go out.">
          <IssueList items={data.blockers} tone="red" testidPrefix="gng-blocker" />
        </Section>
      )}

      {/* HIGH */}
      {data.high_priority?.length > 0 && (
        <Section title="🔴 High-Priority Issues" testid="gng-section-high"
          copy="Should be fixed within Wave 1 (first 7 days).">
          <IssueList items={data.high_priority} tone="yellow" testidPrefix="gng-high" />
        </Section>
      )}

      {/* MEDIUM */}
      {data.medium_priority?.length > 0 && (
        <Section title="🟡 Medium-Priority Issues" testid="gng-section-medium"
          copy="Can wait until after the first cohort.">
          <IssueList items={data.medium_priority} tone="yellow" testidPrefix="gng-medium" />
        </Section>
      )}

      {/* KNOWN ISSUES */}
      {data.known_issues?.length > 0 && (
        <Section title="📋 Known Issues (Accepted Imperfections)" testid="gng-section-known"
          copy="Documented list of items we consciously accept for Wave 1.">
          <IssueList items={data.known_issues} tone="neutral" testidPrefix="gng-known" />
        </Section>
      )}

      {/* SIGNALS */}
      <Section title="Cross-Phase Signals" testid="gng-section-signals">
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:10}}>
          {Object.entries(data.signals || {}).map(([k, v]) => (
            <div key={k} data-testid={`gng-signal-${k}`} style={{
              padding:"12px 14px", background:"#fff", border:"1px solid rgba(184,145,58,0.2)", borderRadius:10,
            }}>
              <div style={{fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase", color:"rgba(27,35,64,0.55)"}}>{k.replace(/_/g," ")}</div>
              <div style={{fontSize:18, fontFamily:"Cormorant Garamond, serif", marginTop:4}}>{v ?? "—"}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* SUCCESS METRICS */}
      <Section title="Success Metrics for Wave 1" testid="gng-section-metrics"
        copy="If these are hit within 7 days, expand to Wave 2 (50–100 testers).">
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:10}}>
          {Object.entries(data.success_metrics || {}).map(([k, v]) => (
            <div key={k} data-testid={`gng-metric-${k}`} style={{
              padding:"12px 14px", background:"rgba(184,145,58,0.06)", border:"1px solid rgba(184,145,58,0.2)", borderRadius:10,
            }}>
              <div style={{fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase", color:"rgba(27,35,64,0.6)"}}>{k.replace(/target_/, "").replace(/_/g," ")}</div>
              <div style={{fontSize:20, fontFamily:"Cormorant Garamond, serif", marginTop:4}}>{v}</div>
            </div>
          ))}
        </div>
      </Section>

      <div style={{marginTop:32, paddingTop:20, borderTop:"1px solid rgba(27,35,64,0.1)", fontSize:11, color:"rgba(27,35,64,0.55)"}}>
        Generated by Métier Alpha Ops · Report id {data.generated_at?.slice(0,19)}
      </div>

      <style>{`@media print {
        .no-print { display: none !important; }
        body { background: white; }
      }`}</style>
    </div>
  );
}

function Section({title, copy, testid, children}) {
  return (
    <div style={{marginBottom:26}} data-testid={testid}>
      <h2 style={{fontFamily:"Cormorant Garamond, serif", fontSize:22, fontWeight:500, margin:"0 0 4px"}}>{title}</h2>
      {copy && <div style={{fontSize:12, color:"rgba(27,35,64,0.6)", marginBottom:10}}>{copy}</div>}
      {children}
    </div>
  );
}

function HeroStat({label, value, testid}) {
  return (
    <div data-testid={testid}>
      <div style={{fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase", opacity:0.65}}>{label}</div>
      <div style={{fontSize:24, fontFamily:"Cormorant Garamond, serif", marginTop:2}}>{value}</div>
    </div>
  );
}

function IssueList({items, tone, testidPrefix}) {
  const accent = tone === "red" ? "#C05353" : tone === "yellow" ? "#B8913A" : "#1B2340";
  return (
    <ol style={{padding:0, margin:0, listStyle:"none"}}>
      {items.map((it, i) => (
        <li key={i} data-testid={`${testidPrefix}-${i}`} style={{
          padding:"10px 14px", background:"#fff", border:"1px solid rgba(184,145,58,0.2)",
          borderLeft:`3px solid ${accent}`, borderRadius:8, marginBottom:6,
        }}>
          <div style={{fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em", color:accent, fontWeight:600}}>{it.area}</div>
          <div style={{fontSize:13, marginTop:3, color:"rgba(27,35,64,0.86)"}}>{it.detail}</div>
        </li>
      ))}
    </ol>
  );
}
